import {useCallback, useEffect, useRef, useState} from 'react';
import {useAuth} from '../../../helpers/AuthContext';

/**
 * Loads the seller's to-ship label queue from GET /v1/seller/labels/queue.
 * Pass `orderUuids` only to narrow the response server-side (at most 200 uuids);
 * the composition root loads the whole queue and preselects instead.
 */
export default function useLabelQueue({orderUuids} = {}) {
  const {fetch: authFetch, API_URL} = useAuth();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const latestRequest = useRef(0);
  const filterKey = Array.isArray(orderUuids) ? orderUuids.join(',') : '';

  const load = useCallback(async () => {
    const request = latestRequest.current + 1;
    latestRequest.current = request;
    setLoading(true);
    setError('');

    try {
      const query = filterKey ? `?orders=${encodeURIComponent(filterKey)}` : '';
      const response = await authFetch(`${API_URL}/v1/seller/labels/queue${query}`);
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body?.message || body?.error || 'Failed to load orders to ship');
      }
      if (request !== latestRequest.current) return;
      setItems(body.data || []);
      setMeta(body.meta || {});
    } catch (loadError) {
      if (request !== latestRequest.current) return;
      setError(loadError.message || 'Failed to load orders to ship');
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  }, [API_URL, authFetch, filterKey]);

  useEffect(() => {
    load();
    return () => {
      latestRequest.current += 1;
    };
  }, [load]);

  return {items, meta, loading, error, reload: load};
}
